import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { DUPLICATE_PAIR, RADIUS, SHADOW, SURFACE } from "../../../theme/tokens";
import type { SuspicionReason } from "../../../utils/nurseMergeModels";
import MetaChip from "../../UIToolkit/Badges/MetaChip";
import { describeReasons } from "./duplicatePairs";

/** Up to three reasons stack in one column (they fit the row's height); more fold into two. */
const MAX_CHIPS_PER_COLUMN = 3;

type Props = {
	reasons: readonly SuspicionReason[];
	dataTestId: string;
};

/**
 * "Why it's suggested", drawn as the link between the two records: a brand hairline from one side to the other
 * with the reasons on it. Match evidence carries the brand tint; supporting evidence recedes to neutral, so a
 * pair suggested on "Same phone" never reads as weakly as one suggested on "One inactive" alone.
 */
export const DuplicateReasonBridge: FC<Props> = ({ reasons, dataTestId }) => {
	const { t } = useTranslation();
	const chips = describeReasons(reasons);

	return (
		<Box
			data-testid={dataTestId}
			sx={(theme) => {
				const line = alpha(theme.palette.primary.main, DUPLICATE_PAIR.BRIDGE_LINE_ALPHA);
				return {
					position: "relative",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					width: "100%",
					height: "100%",
					lineHeight: "normal",
					"&::before": {
						content: '""',
						position: "absolute",
						insetInline: 0,
						top: "50%",
						height: DUPLICATE_PAIR.BRIDGE_LINE_WIDTH_PX,
						transform: "translateY(-50%)",
						background: `linear-gradient(90deg, transparent, ${line} ${DUPLICATE_PAIR.BRIDGE_LINE_FADE_STOP}, ${line} calc(100% - ${DUPLICATE_PAIR.BRIDGE_LINE_FADE_STOP}), transparent)`,
					},
				};
			}}
		>
			<Box
				component="ul"
				aria-label={t("duplicates.reasons.aria", { reasons: chips.map((chip) => t(chip.labelKey)).join(", ") })}
				sx={(theme) => ({
					position: "relative",
					display: "grid",
					// A short stack keeps the node narrow, so the line either side of it stays long enough to read as a link.
					gridTemplateColumns: chips.length > MAX_CHIPS_PER_COLUMN ? "repeat(2, auto)" : "auto",
					justifyItems: "center",
					gap: 0.5,
					maxWidth: `calc(100% - ${2 * DUPLICATE_PAIR.BRIDGE_STUB_PX}px)`,
					m: 0,
					p: 0.625,
					listStyle: "none",
					backgroundColor: SURFACE,
					border: `1px solid ${alpha(theme.palette.primary.main, DUPLICATE_PAIR.BRIDGE_NODE_BORDER_ALPHA)}`,
					borderRadius: RADIUS.CONTROL / RADIUS.CARD,
					boxShadow: SHADOW.CONTROL_RAISED,
				})}
			>
				{chips.map((chip) => (
					<Box component="li" key={chip.code} sx={{ display: "inline-flex", minWidth: 0 }}>
						<MetaChip
							size="small"
							label={t(chip.labelKey)}
							tooltip={t(chip.hintKey)}
							tone={chip.strength === "match" ? "primary" : "neutral"}
							dataTestId={`${dataTestId}-${chip.code}`}
						/>
					</Box>
				))}
			</Box>
		</Box>
	);
};
